import React from 'react';
import { PurchaseOrderDocument } from '../types';
import { purchaseOrderDocumentAPI } from '../services/api';

interface DocumentListProps {
  documents: PurchaseOrderDocument[];
  onDelete: () => void;
}

const DocumentList: React.FC<DocumentListProps> = ({ documents, onDelete }) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getFileIcon = (type: string): string => {
    if (type.includes('pdf')) return '📄';
    if (type.includes('image')) return '🖼️';
    if (type.includes('word') || type.includes('document')) return '📝';
    return '📎';
  };

  const handleDownload = async (doc: PurchaseOrderDocument) => {
    try {
      const response = await purchaseOrderDocumentAPI.download(doc.id!);
      const blob = new Blob([response.data], { type: doc.file_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download document', err);
      alert('Failed to download document');
    }
  };

  const handleDelete = async (doc: PurchaseOrderDocument) => {
    if (!window.confirm(`Are you sure you want to delete "${doc.file_name}"?`)) {
      return;
    }

    try {
      await purchaseOrderDocumentAPI.delete(doc.id!);
      onDelete();
    } catch (err) {
      console.error('Failed to delete document', err);
      alert('Failed to delete document');
    }
  };

  if (documents.length === 0) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#7f8c8d',
        backgroundColor: '#fafafa',
        borderRadius: '8px'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📂</div>
        <p>No documents uploaded yet</p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {documents.map((doc) => (
          <div
            key={doc.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              transition: 'box-shadow 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
              <span style={{ fontSize: '2rem' }}>{getFileIcon(doc.file_type)}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  {doc.file_name}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>
                  {formatFileSize(doc.file_size)} • Uploaded {formatDate(doc.uploaded_at!)}
                  {doc.uploaded_by_username && ` by ${doc.uploaded_by_username}`}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => handleDownload(doc)}
                className="btn btn-secondary btn-small"
                title="Download"
              >
                ⬇️ Download
              </button>
              <button
                onClick={() => handleDelete(doc)}
                className="btn btn-danger btn-small"
                title="Delete"
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentList;

